import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaFutbol,
  FaBasketballBall,
  FaVolleyballBall,
  FaCheck,
  FaArrowRight,
  FaTrophy,
  FaShieldAlt,
} from "react-icons/fa";
import { MdSportsCricket } from "react-icons/md";
import { HiSparkles } from "react-icons/hi2";

const SportsSelection = () => {
  const navigate = useNavigate();
  const [activeSportId, setActiveSportId] = useState("soccer");
  const [selectedMatrixPkg, setSelectedMatrixPkg] = useState("allStar");

  const sportsData = {
    soccer: {
      id: "soccer",
      name: "INDOOR SOCCER",
      tabLabel: "INDOOR SOCCER",
      icon: <FaFutbol />,
      tagline: "FAST-PACED TURF ACTION, MINI-TOURNAMENTS & PENALTY SHOOTOUTS",
      description:
        "Played on our indoor premium pro-turf field with official goals, corner flags, match balls, and scrimmage pinnies. Perfect for scrimmages, skills challenges, and penalty-kick shootouts.",
      specs: "Pro Turf Arena Field with perimeter netting",
      gear: [
        "FIFA-grade match balls",
        "Team scrimmage pinnies",
        "Official goals & cones",
        "Referee whistle & scoreboard",
      ],
      idealFor:
        "Youth teams, high-energy birthdays, adult league social mixers",
      availability: {
        smallSquad: {
          status: "INCLUDED",
          statusColor: "text-emerald-600 bg-emerald-50 border-emerald-200",
          note: "Included by default",
        },
        allStar: {
          status: "INCLUDED CHOICE",
          statusColor: "text-primaryBtnHoverColor bg-red-50 border-red-200",
          isPopular: true,
          note: "Select Indoor Soccer as your featured sport for 90 full minutes.",
        },
        bigLeague: {
          status: "INCLUDED CHOICE",
          statusColor: "text-primaryBtnHoverColor bg-red-50 border-red-200",
          note: "Select Indoor Soccer with full court priority and tournament brackets.",
        },
      },
    },
    basketball: {
      id: "basketball",
      name: "HARDWOOD BASKETBALL",
      tabLabel: "HARDWOOD BASKETBALL",
      icon: <FaBasketballBall />,
      tagline: "FULL-COURT RUNS, 3-POINT SHOOTOUTS & DUNK CONTESTS",
      description:
        "Hit our regulation hardwood court equipped with break-away rims, adjustable backboard heights for youth parties, electronic scoreboard, and official match balls.",
      specs:
        "Regulation Hardwood Court with shock absorption & boundary buffers",
      gear: [
        "Official size 6 & 7 basketballs",
        "Adjustable breakaway rims",
        "Electronic digital scoreboard",
        "Team reversible scrimmage jerseys",
      ],
      idealFor:
        "Basketball team parties, pickup tournaments, shooting challenges",
      availability: {
        smallSquad: {
          status: "ADD-ON AVAILABLE",
          statusColor: "text-slate-600 bg-slate-100 border-slate-200",
          note: "Upgrade available for Small Squad groups upon request.",
        },
        allStar: {
          status: "INCLUDED CHOICE",
          statusColor: "text-primaryBtnHoverColor bg-red-50 border-red-200",
          isPopular: true,
          note: "Choose Hardwood Basketball as your featured 90-min activity.",
        },
        bigLeague: {
          status: "INCLUDED CHOICE",
          statusColor: "text-primaryBtnHoverColor bg-red-50 border-red-200",
          note: "Full court priority with bracket setup and referee support.",
        },
      },
    },
    volleyball: {
      id: "volleyball",
      name: "INDOOR VOLLEYBALL",
      tabLabel: "INDOOR VOLLEYBALL",
      icon: <FaVolleyballBall />,
      tagline: "RALLY-READY SPIKES, SERVE CHALLENGES & SOCIAL TOURNAMENTS",
      description:
        "High-tension Olympic standard net setup on court flooring with non-marking grip. Great for casual party rallies, bump-set-spike clinics, or competitive team round-robins.",
      specs: "Impact-cushioned arena surface with regulation court lines",
      gear: [
        "Soft-touch tournament volleyballs",
        "Olympic regulation high-tension net",
        "Padded boundary antennas & posts",
        "Referee stand & flip scorekeeper",
      ],
      idealFor: "Family reunions, co-ed groups, team celebration mixers",
      availability: {
        smallSquad: {
          status: "ADD-ON AVAILABLE",
          statusColor: "text-slate-600 bg-slate-100 border-slate-200",
          note: "Upgrade available for Small Squad groups upon request.",
        },
        allStar: {
          status: "INCLUDED CHOICE",
          statusColor: "text-primaryBtnHoverColor bg-red-50 border-red-200",
          isPopular: true,
          note: "Choose Indoor Volleyball as your featured 90-min activity.",
        },
        bigLeague: {
          status: "INCLUDED CHOICE",
          statusColor: "text-primaryBtnHoverColor bg-red-50 border-red-200",
          note: "Full court priority, multi-ball rally setup, and tournament host.",
        },
      },
    },
    cricket: {
      id: "cricket",
      name: "INDOOR CRICKET",
      tabLabel: "INDOOR CRICKET",
      icon: <MdSportsCricket />,
      tagline: "PITCH ACTION, TAPE-BALL / LEATHER-BALL BATTING & BOWLING",
      description:
        "Enclosed indoor cricket arena with full protective perimeter netting, authentic bowling creases, and bounce-true artificial turf for exciting 6-a-side matches.",
      specs: "High-density cricket matting with 360° safety perimeter netting",
      gear: [
        "Heavy tape-balls & safety cricket balls",
        "English & Kashmir willow bats",
        "Spring-loaded safety wickets",
        "Digital overs & runs tracker",
      ],
      idealFor:
        "Cricket clubs, batting showdowns, high-energy group competitions",
      availability: {
        smallSquad: {
          status: "ADD-ON AVAILABLE",
          statusColor: "text-slate-600 bg-slate-100 border-slate-200",
          note: "Upgrade available for Small Squad groups upon request.",
        },
        allStar: {
          status: "INCLUDED CHOICE",
          statusColor: "text-primaryBtnHoverColor bg-red-50 border-red-200",
          isPopular: true,
          note: "Choose Indoor Cricket as your featured 90-min activity.",
        },
        bigLeague: {
          status: "INCLUDED CHOICE",
          statusColor: "text-primaryBtnHoverColor bg-red-50 border-red-200",
          note: "Full pitch exclusivity, overs rotation, and umpire support.",
        },
      },
    },
  };

  const activeSport = sportsData[activeSportId];

  const handleRequestSport = (sportName) => {
    navigate("/party-request", {
      state: { selectedSport: sportName, selectedPackage: selectedMatrixPkg },
    });
  };

  return (
    <div className="w-full py-20 px-4 md:px-8 lg:px-12 bg-[#050733] select-none text-white border-t border-white/10">
      <div className="max-w-7xl mx-auto flex flex-col items-center">
        {/* Section Heading */}
        <h2 className="text-3xl sm:text-5xl md:text-6xl font-heading font-black text-white uppercase text-center tracking-tight leading-tight">
          CHOOSE YOUR SPORT
        </h2>

        {/* Subtitle */}
        <p className="mt-4 text-gray-300 font-subheading text-base sm:text-lg max-w-3xl text-center leading-relaxed">
          Four premier sporting arenas under one roof. We don't just give you an
          open floor—we give you regulation equipment, pro surfaces, and real
          game atmosphere.
        </p>

        {/* Inclusions Notice Banner */}
        <div className="mt-8 bg-[#080c30] border border-white/10 rounded-xl py-3 px-5 max-w-2xl mx-auto text-xs sm:text-sm font-subheading text-gray-300 flex flex-wrap items-center justify-center gap-2 text-center shadow-md">
          <span className="flex items-center gap-1.5 font-bold text-white">
            <HiSparkles className="text-primaryBtnHoverColor text-sm" />
            Small Squad:
          </span>
          <span>Soccer included</span>
          <span className="text-gray-500 font-bold hidden sm:inline">
            &bull;
          </span>
          <span className="flex items-center gap-1.5 font-bold text-primaryBtnHoverColor">
            <FaTrophy className="text-xs" />
            All-Star &amp; Big League:
          </span>
          <span>Choose 1 of 4 sports (or add a 2nd!)</span>
        </div>

        {/* 4 Sport Selection Tabs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 w-full max-w-5xl mx-auto mt-10">
          {Object.values(sportsData).map((sport) => {
            const isActive = activeSportId === sport.id;

            return (
              <button
                key={sport.id}
                onClick={() => setActiveSportId(sport.id)}
                className={`py-3.5 px-4 rounded-xl font-heading font-bold text-xs sm:text-sm tracking-wide uppercase transition-all duration-200 flex items-center justify-center gap-2.5 cursor-pointer ${
                  isActive
                    ? "bg-primaryBtnHoverColor text-white shadow-lg shadow-red-950/40 scale-[1.02]"
                    : "bg-[#080c30] hover:bg-[#0c1244] text-gray-300 border border-white/10 hover:border-white/25"
                }`}
              >
                <span className="text-base sm:text-lg">{sport.icon}</span>
                <span>{sport.tabLabel}</span>
              </button>
            );
          })}
        </div>

        {/* Active Sport Detail 2-Column Card */}
        <div className="w-full max-w-6xl mx-auto mt-8 bg-[#080c30] border border-white/10 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            {/* Left Column: Details & Specs (7 cols) */}
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div>
                {/* Header with Icon */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 shadow-inner flex items-center justify-center text-primaryBtnHoverColor text-2xl flex-shrink-0">
                    {activeSport.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-heading font-extrabold text-white uppercase tracking-tight">
                      {activeSport.name}
                    </h3>
                    <p className="text-xs sm:text-sm font-heading font-bold text-primaryBtnHoverColor uppercase tracking-wider mt-0.5">
                      {activeSport.tagline}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-300 font-subheading text-sm sm:text-base leading-relaxed mt-5">
                  {activeSport.description}
                </p>

                {/* Surface & Court Specs */}
                <div className="bg-[#060927] border border-white/10 rounded-xl p-4 my-6 shadow-inner">
                  <div className="flex items-center gap-2 text-xs font-heading font-bold text-primaryBtnHoverColor uppercase tracking-wider">
                    <FaShieldAlt />
                    <span>ARENA SURFACE &amp; COURT SPECS</span>
                  </div>
                  <div className="text-white font-heading font-bold text-sm sm:text-base mt-1.5">
                    {activeSport.specs}
                  </div>
                </div>

                {/* Pro Gear & Equipment */}
                <div>
                  <h4 className="text-xs font-heading font-extrabold text-gray-400 uppercase tracking-wider mb-3">
                    PRO GEAR &amp; EQUIPMENT INCLUDED:
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {activeSport.gear.map((gearItem, idx) => (
                      <div
                        key={idx}
                        className="bg-[#060927] border border-white/10 rounded-lg p-3 flex items-center gap-2.5 text-xs sm:text-sm font-subheading text-gray-300 font-medium shadow-2xs"
                      >
                        <FaCheck className="text-primaryBtnHoverColor flex-shrink-0 text-xs" />
                        <span>{gearItem}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom Ideal for + CTA */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-8 pt-6 border-t border-white/10">
                <div className="text-xs font-subheading text-gray-400 max-w-sm">
                  <strong className="text-white block mb-0.5">
                    Ideal for:
                  </strong>
                  {activeSport.idealFor}
                </div>

                <button
                  onClick={() => handleRequestSport(activeSport.name)}
                  className="w-full sm:w-auto bg-primaryBtnHoverColor hover:bg-red-800 text-white font-heading font-bold text-xs sm:text-sm px-6 py-3.5 rounded-lg shadow-lg shadow-red-950/40 transition duration-200 flex items-center justify-center gap-2 tracking-wider uppercase cursor-pointer hover:scale-[1.02] active:scale-95 flex-shrink-0"
                >
                  <span>REQUEST WITH {activeSport.name}</span>
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>

            {/* Right Column: Package Availability Matrix (5 cols) */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              <h4 className="text-xs font-heading font-extrabold text-white uppercase tracking-wider">
                PACKAGE AVAILABILITY MATRIX
              </h4>

              {/* Small Squad card */}
              <div
                onClick={() => setSelectedMatrixPkg("smallSquad")}
                className={`rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                  selectedMatrixPkg === "smallSquad"
                    ? "bg-[#0c1244] border-2 border-primaryBtnHoverColor shadow-md"
                    : "bg-[#060927] border border-white/10 hover:border-white/25 shadow-sm"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-bold text-sm text-white">
                      Small Squad ($275)
                    </span>
                    {selectedMatrixPkg === "smallSquad" && (
                      <span className="bg-primaryBtnHoverColor text-white text-[9px] font-heading font-bold px-1.5 py-0.5 rounded uppercase">
                        SELECTED
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-[10px] font-heading font-bold px-2 py-0.5 rounded border uppercase ${
                      activeSport.availability.smallSquad.status === "INCLUDED"
                        ? "text-emerald-400 bg-emerald-950/40 border-emerald-500/30"
                        : "text-gray-400 bg-white/5 border-white/10"
                    }`}
                  >
                    {activeSport.availability.smallSquad.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400 font-subheading mt-1.5 leading-relaxed">
                  {activeSport.availability.smallSquad.note}
                </p>
              </div>

              {/* All-Star Squad card */}
              <div
                onClick={() => setSelectedMatrixPkg("allStar")}
                className={`rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                  selectedMatrixPkg === "allStar"
                    ? "bg-[#0c1244] border-2 border-primaryBtnHoverColor shadow-md"
                    : "bg-[#060927] border border-white/10 hover:border-white/25 shadow-sm"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-bold text-sm text-white">
                      All-Star Squad ($375)
                    </span>
                    {selectedMatrixPkg === "allStar" ? (
                      <span className="bg-primaryBtnHoverColor text-white text-[9px] font-heading font-bold px-1.5 py-0.5 rounded uppercase">
                        SELECTED
                      </span>
                    ) : (
                      <span className="bg-white/10 text-gray-300 text-[9px] font-heading font-bold px-1.5 py-0.5 rounded uppercase">
                        POPULAR
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-heading font-bold px-2 py-0.5 rounded border uppercase text-primaryBtnHoverColor bg-red-950/40 border-red-500/30">
                    {activeSport.availability.allStar.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400 font-subheading mt-1.5 leading-relaxed">
                  {activeSport.availability.allStar.note}
                </p>
              </div>

              {/* Big League card */}
              <div
                onClick={() => setSelectedMatrixPkg("bigLeague")}
                className={`rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                  selectedMatrixPkg === "bigLeague"
                    ? "bg-[#0c1244] border-2 border-primaryBtnHoverColor shadow-md"
                    : "bg-[#060927] border border-white/10 hover:border-white/25 shadow-sm"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-bold text-sm text-white">
                      Big League ($475)
                    </span>
                    {selectedMatrixPkg === "bigLeague" && (
                      <span className="bg-primaryBtnHoverColor text-white text-[9px] font-heading font-bold px-1.5 py-0.5 rounded uppercase">
                        SELECTED
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-heading font-bold px-2 py-0.5 rounded border uppercase text-primaryBtnHoverColor bg-red-950/40 border-red-500/30">
                    {activeSport.availability.bigLeague.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400 font-subheading mt-1.5 leading-relaxed">
                  {activeSport.availability.bigLeague.note}
                </p>
              </div>

              {/* Want to play two sports callout box */}
              <div className="bg-[#0b103b] border border-primaryBtnHoverColor/40 rounded-xl p-4 mt-2">
                <p className="text-xs font-subheading text-gray-300 leading-relaxed">
                  <strong className="text-primaryBtnHoverColor font-heading block mb-1">
                    Want to play two sports?
                  </strong>
                  You can split your 90 minutes across two activities (e.g. 45
                  min Soccer + 45 min Basketball) for just a small field
                  transition add-on.
                </p>
              </div>

              {/* Testimonial Quote */}
              <p className="text-[11px] italic text-gray-400 text-center font-subheading mt-3 px-2">
                "Our court coordinators organize scrimmages and mini-tournaments
                so parents don't have to referee!"
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SportsSelection;
